import { API_URL } from "../../config";

/**
 * Weather client — talks to our keyless Open-Meteo proxy (`/weather/*`). Plain
 * fetch (no RTK/store dependency) so the SHARED WeatherWidget works everywhere
 * it renders: template designer, Preview modal, and the web player. Current
 * weather is cached in-memory per (lat,lon,units) so re-renders / dragging a
 * zone never refetch; the widget also refreshes on a timer.
 */

export interface WeatherLocation {
  id?: number | null;
  name: string;
  admin1?: string | null;
  country?: string | null;
  country_code?: string | null;
  latitude: number;
  longitude: number;
  timezone?: string | null;
  display_name: string;
}

export interface CurrentWeather {
  temperature: number;
  feels_like?: number | null;
  unit: string;
  weather_code: number;
  condition: string;
  is_day: boolean;
  humidity?: number | null;
  wind?: number | null;
  wind_unit?: string;
  fetched_at?: string;
}

/** Refresh interval + cache TTL for current weather (10 minutes). */
export const WEATHER_TTL_MS = 10 * 60 * 1000;

export async function searchWeatherLocations(query: string): Promise<WeatherLocation[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  try {
    const res = await fetch(`${API_URL}/weather/search?q=${encodeURIComponent(q)}`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json?.data) ? (json.data as WeatherLocation[]) : [];
  } catch {
    return [];
  }
}

const currentCache = new Map<string, { at: number; data: CurrentWeather }>();

export async function fetchCurrentWeather(
  lat: number, lon: number, units: "metric" | "imperial",
): Promise<CurrentWeather | null> {
  const key = `${lat.toFixed(4)},${lon.toFixed(4)},${units}`;
  const hit = currentCache.get(key);
  if (hit && Date.now() - hit.at < WEATHER_TTL_MS) return hit.data;
  try {
    const res = await fetch(
      `${API_URL}/weather/current?lat=${lat}&lon=${lon}&units=${units}`,
      { headers: { Accept: "application/json" } },
    );
    if (!res.ok) return null;
    const json = await res.json();
    const data = json?.data as CurrentWeather | undefined;
    if (!data || typeof data.temperature !== "number") return null;
    currentCache.set(key, { at: Date.now(), data });
    return data;
  } catch {
    return null;
  }
}

/** WMO weather-code → emoji (day/night aware). */
export function weatherEmoji(code: number, isDay: boolean): string {
  if (code === 0) return isDay ? "☀️" : "🌙";
  if (code === 1) return isDay ? "🌤️" : "🌙";
  if (code === 2) return isDay ? "⛅" : "☁️";
  if (code === 3) return "☁️";
  if (code === 45 || code === 48) return "🌫️";
  if (code >= 51 && code <= 57) return "🌦️";
  if (code >= 61 && code <= 67) return "🌧️";
  if (code >= 71 && code <= 77) return "🌨️";
  if (code >= 80 && code <= 82) return "🌧️";
  if (code === 85 || code === 86) return "🌨️";
  if (code >= 95) return "⛈️";
  return "🌡️";
}
